import { fetchAdminUserLandingEventStats } from '@/api/admin';
import PeriodFilter from '@/components/stats/period-filter';
import { Spinner } from '@/components/ui/spinner';
import type { StatsPeriod } from '@/entities/admin-stats.entity';
import type { ILandingEventStats } from '@/entities/landing.entity';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import LandingEventsStatsView from '@/pages/landings/events/landing-events-stats-view';
import LandingEventsDataSkeleton from '@/pages/landings/events/landing-events-skeleton';
import { useCallback, useEffect, useState } from 'react';

interface LandingsStatsSubtabProps {
	profileId: number;
	fetchStats?: (
		profileId: number,
		params?: { period?: StatsPeriod; linkId?: string },
	) => Promise<ILandingEventStats>;
}

export default function LandingsStatsSubtab({
	profileId,
	fetchStats = fetchAdminUserLandingEventStats,
}: LandingsStatsSubtabProps) {
	const [period, setPeriod] = useState<StatsPeriod>('week');
	const [stats, setStats] = useState<ILandingEventStats | null>(null);
	const [error, setError] = useState(false);
	const queryKey = `${profileId}:${period}`;
	const [resolvedKey, setResolvedKey] = useState<string | null>(null);
	const isLoading = resolvedKey !== queryKey;
	const displayStats = resolvedKey === queryKey ? stats : null;

	useEffect(() => {
		let cancelled = false;

		fetchStats(profileId, { period })
			.then(data => {
				if (!cancelled) {
					setStats(data);
					setError(false);
					setResolvedKey(queryKey);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setStats(null);
					setError(true);
					setResolvedKey(queryKey);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [fetchStats, profileId, period, queryKey]);

	const retryLoad = useCallback(() => {
		setResolvedKey(null);
		setError(false);
		fetchStats(profileId, { period })
			.then(data => {
				setStats(data);
				setResolvedKey(queryKey);
			})
			.catch(() => {
				setStats(null);
				setError(true);
				setResolvedKey(queryKey);
			});
	}, [fetchStats, profileId, period, queryKey]);

	if (isLoading && !displayStats) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error && resolvedKey === queryKey) {
		return (
			<LandingsErrorState
				message='Лендинги недоступны. Проверьте сервис landing и LANDING_INTERNAL_SECRET.'
				onRetry={retryLoad}
			/>
		);
	}

	if (!displayStats) {
		return null;
	}

	return (
		<div className='flex flex-col gap-4'>
			<PeriodFilter period={period} onChange={setPeriod} />
			{isLoading ? <LandingEventsDataSkeleton /> : <LandingEventsStatsView stats={displayStats} />}
		</div>
	);
}
